import NiceModal from "@ebay/nice-modal-react";
import { useRecoilValue } from "recoil";

import SimpleModal from "components/modals/SimpleModal";
import { userState } from "store/store";
import { File as FileType, Question, TErrCodes } from "types/types";
import {
  useFetchData,
  useFetchPaginatedData,
  useGenericMutation,
} from "../useAxios";

export const useGetQuestions = ({
  page,
  size,
  status,
  startDate,
  endDate,
  course,
  subjectType,
  questionType,
  questionStatus,
  searchType,
  searchText,
  sort,
  questionBNo,
  mine,
  transfer,
  monitoring,
  activeBNo,
}: {
  page: number;
  size: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  course?: string;
  subjectType?: string;
  questionType?: string;
  questionStatus?: string;
  searchType?: string;
  searchText?: string;
  sort?: string;
  questionBNo?: string;
  mine?: number;
  transfer?: number;
  monitoring?: number;
  activeBNo?: string;
}) =>
  useFetchPaginatedData<Question>({
    endpoint: "v1/questions",
    params: {
      page,
      size,
      sort,
      startDate,
      endDate,
      course,
      subjectType,
      questionType,
      questionStatus,
      searchType,
      searchText,
      status,
      questionBNo,
      mine,
      transfer,
      monitoring,
      activeBNo,
    },
    queryKey: [
      "questions",
      mine
        ? "mine"
        : monitoring
          ? "monitoring"
          : transfer
            ? "transfer"
            : "default",
      status ?? "",
      startDate ?? "",
      endDate ?? "",
      course ?? "all",
      subjectType ?? "all",
      questionType ?? "all",
      questionStatus ?? "all",
      searchType ?? "",
      searchText ?? "",
      sort ?? "createdDesc",
      page,
      size,
      questionBNo ?? "",
      // activeBNo ?? "",
    ],
    keepPreviousData: true,
    showLoading: !questionBNo && !activeBNo,
  });

export const useGetQuestion = ({
  questionId,
  ...rest
}: {
  questionId?: string;
}) =>
  useFetchData<Question>({
    params: rest,
    endpoint: `v1/questions/${questionId}`,
    queryKey: ["question", questionId ?? ""],
    showLoading: true,
    enabled: !!questionId,
    keepPreviousData: !!questionId,
  });

export const useGetCourses = ({
  status,
  startDate,
  endDate,
  mine,
  transfer,
  monitoring,
}: {
  status?: string;
  startDate: string;
  endDate: string;
  mine?: number;
  transfer?: number;
  monitoring?: number;
}) =>
  useFetchData<Array<{ value: string; label: string }>>({
    params: { status, startDate, endDate, mine, transfer, monitoring },
    endpoint: `v1/questions/courses`,
    queryKey: [
      "courses",
      mine
        ? "mine"
        : monitoring
          ? "monitoring"
          : transfer
            ? "transfer"
            : "default",
      status ?? "",
      startDate ?? "",
      endDate ?? "",
    ],
  });

export const useGetBonusQuestions = () =>
  useFetchData<Question[]>({
    endpoint: "v1/questions/bonus",
    queryKey: ["questions", "bonus"],
    showLoading: false,
  });

export const useGetQuestionCount = () =>
  useFetchData<{ onGoingCount: number; transferCount: number }>({
    endpoint: `v1/questions/counts`,
    queryKey: ["questionCount"],
    meta: { errCode: TErrCodes.NONE },
  });

export const useGetMonitoringCount = () =>
  useFetchData<{ onGoingCount: number }>({
    endpoint: `v1/questions/monitoringCount`,
    queryKey: ["monitoringCount"],
    meta: { errCode: TErrCodes.NONE },
  });

export const useWriteAdvice = () =>
  useGenericMutation<{
    questionBNo: string;
    content: string;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/advices`,
    invalidateQueries: (_, { questionBNo }) => [["question", questionBNo]],
  });

export const useDeleteAdvice = () =>
  useGenericMutation<{
    id: number;
    questionBNo: string;
  }>({
    method: "delete",
    endpoint: ({ id }) => `v1/advices/${id}`,
    invalidateQueries: (_, { questionBNo }) => [["question", questionBNo]],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "조언 삭제",
        message: "삭제 완료되었습니다.",
        showCancel: false,
      });
    },
  });

export const useWriteAnswer = () =>
  useGenericMutation<{
    questionBNo: string;
    title: string;
    responderName: string;
    content: string;
    classificationType: string;
    classificationDifficulty: string;
    files: File[];
  }>({
    contentType: "multipart/form-data",
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/answers`,
    invalidateQueries: (_, { questionBNo }) => [
      ["questionCount"],
      ["question", questionBNo],
      ["auth/this"],
      ["auth/info"],
      ["main"],
    ],
    invalidateQueriesOnError: true,
    dataTransform: (form) => {
      const formData = new FormData();

      form.files.forEach((file) => {
        formData.append("files", file, file.name);
      });
      formData.append("title", form.title);
      formData.append("responderName", form.responderName);
      formData.append("content", form.content);
      formData.append("classificationType", form.classificationType);
      formData.append(
        "classificationDifficulty",
        form.classificationDifficulty,
      );

      return formData;
    },
  });

export const useTransferQuestion = () =>
  useGenericMutation<{
    questionBNo: string;
    content: string;
    files: File[];
  }>({
    contentType: "multipart/form-data",
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/transfers`,
    invalidateQueries: (_, { questionBNo }) => [
      ["questionCount"],
      ["question", questionBNo],
    ],
    invalidateQueriesOnError: true,
    dataTransform: (form) => {
      const formData = new FormData();

      form.files.forEach((file) => {
        formData.append("files", file, file.name);
      });
      formData.append("content", form.content);

      return formData;
    },
  });

export const useCompleteInspection = () =>
  useGenericMutation<{
    questionBNo: string;
    status: number;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/inspection`,
    invalidateQueries: (_, { questionBNo }) => [
      ["monitoringCount"],
      ["question", questionBNo],
    ],
    invalidateQueriesOnError: true,
  });

export const useReport = () =>
  useGenericMutation<{
    questionBNo: string;
    report: string;
    title: string;
    responderName: string;
    content: string;
    classificationType: string;
    classificationDifficulty: string;
    files: File[];
  }>({
    contentType: "multipart/form-data",
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/report`,
    invalidateQueries: (_, { questionBNo }) => [
      ["monitoringCount"],
      ["question", questionBNo],
      ["auth/this"],
      ["auth/info"],
      ["main"],
    ],
    invalidateQueriesOnError: true,
    dataTransform: (form) => {
      const formData = new FormData();

      form.files.forEach((file) => {
        formData.append("files", file, file.name);
      });
      formData.append("report", form.report);
      formData.append("title", form.title);
      formData.append("responderName", form.responderName);
      formData.append("content", form.content);
      formData.append("classificationType", form.classificationType);
      formData.append(
        "classificationDifficulty",
        form.classificationDifficulty,
      );

      return formData;
    },
  });

export const useUpdateStatus = () => {
  const user = useRecoilValue(userState);

  return useGenericMutation<{
    questionBNo: string;
    status: number;
    showLoading?: boolean;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/status`,
    invalidateQueries: [1, 2].includes(user!.grade.id) ? [["questions"]] : [],
    // onSuccess: (_, { questionBNo }) => {
    //   const queryInfo = queryClient.getQueryState([
    //     "questions",
    //     "default",
    //     "대기중",
    //     "",
    //     "",
    //     "all",
    //     "all",
    //     "all",
    //     "대기중",
    //     "",
    //     "",
    //     "createdDesc",
    //     1,
    //     10,
    //     "",
    //     questionBNo,
    //   ]);

    //   if (queryInfo) {
    //     const lastUpdated = queryInfo.dataUpdatedAt;

    //     console.log(Date.now() - lastUpdated);
    //     if (Date.now() - lastUpdated > 1000) {
    //       queryClient.invalidateQueries({ queryKey: ["questions"] });
    //     } else {
    //       const queries = queryClient.getQueryCache().findAll({
    //         queryKey: ["questions"],
    //         exact: false,
    //       });

    //       const queriesToInvalidate = queries.filter(({ queryKey }) => {
    //         const activeBNo = queryKey[queryKey.length - 1];
    //         return activeBNo === "";
    //       });

    //       queriesToInvalidate.forEach((query) => {
    //         queryClient.invalidateQueries({ queryKey: query.queryKey });
    //       });
    //     }
    //   } else {
    //     queryClient.invalidateQueries({ queryKey: ["questions"] });
    //   }
    // },
    showLoading: ({ showLoading }) => !!showLoading,
  });
};
export const useUpdateMonitoringStatus = () =>
  useGenericMutation<{
    questionBNo: string;
    status: number;
    showLoading?: boolean;
  }>({
    endpoint: ({ questionBNo }) =>
      `v1/questions/${questionBNo}/monitoring/status`,
    invalidateQueries: [["questions"]],
    showLoading: ({ showLoading }) => !!showLoading,
  });

export const useUpdateAnswer = () =>
  useGenericMutation<{
    questionBNo: string;
    title: string;
    responderName: string;
    content: string;
    classificationType: string;
    classificationDifficulty: string;
    files: File[];
    oldFiles?: FileType[];
  }>({
    contentType: "multipart/form-data",
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/answers/update`,
    invalidateQueries: (_, { questionBNo }) => [
      ["question", questionBNo],
      ["main"],
    ],
    invalidateQueriesOnError: true,
    dataTransform: (form) => {
      const formData = new FormData();

      form.files.forEach((file) => {
        formData.append("files", file, file.name);
      });
      formData.append("title", form.title);
      formData.append("responderName", form.responderName);
      formData.append("content", form.content);
      formData.append("classificationType", form.classificationType);
      formData.append(
        "classificationDifficulty",
        form.classificationDifficulty,
      );
      form.oldFiles?.forEach?.((file) => {
        formData.append("oldFiles[]", file.fileno);
      });

      return formData;
    },
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "QnA현황",
        message: "답변이 수정되었습니다.",
        showCancel: false,
      });
    },
  });

export const useDeleteAnswer = () =>
  useGenericMutation<{
    questionBNo: string;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/answers/delete`,
    invalidateQueries: (_, { questionBNo }) => [
      ["question", questionBNo],
      ["questions"],
    ],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "QnA현황",
        message: "답변이 삭제되었습니다.",
        showCancel: false,
      });
    },
  });

export const useDeactivateQuestion = () =>
  useGenericMutation<{
    questionBNo: string;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/deactivate`,
    invalidateQueries: (_, { questionBNo }) => [
      ["questions"],
      ["question", questionBNo],
    ],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "QnA현황",
        message: "질문이 비활성화되었습니다.",
        showCancel: false,
      });
    },
  });

export const useActivateQuestion = () =>
  useGenericMutation<{
    questionBNo: string;
  }>({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/activate`,
    invalidateQueries: (_, { questionBNo }) => [
      ["questions"],
      ["question", questionBNo],
    ],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "QnA현황",
        message: "질문이 활성화되었습니다.",
        showCancel: false,
      });
    },
  });

export const useGetQuestionStatus = () =>
  useGenericMutation<
    {
      activeBNo: string;
      questionBNo: string;
    },
    { status: string }
  >({
    endpoint: ({ questionBNo }) => `v1/questions/${questionBNo}/status/check`,
    invalidateQueries: (data) =>
      data?.status === "작성중" ? [["questionCount"], ["questions"]] : [],
    invalidateQueriesOnError: true,
    endLoading: (data) => data?.status === "작성중",
  });
